(() => {
  const registry = window.NipponVinoWineAtlasRenderers = window.NipponVinoWineAtlasRenderers || {};
  const Base = registry.Base;
  if (!Base) return;

  class FixedMapRenderer extends Base {
    async mount(container, data, state = {}) {
      super.mount(container, data, state);
      this.controller.dataset.mapRenderer = 'fixed-v3';
      this.controller.dataset.atlasLayout = this.getLayoutName();
      const response = await fetch(data.mapUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`SVG-Karte konnte nicht geladen werden (${response.status})`);
      const markup = await response.text();
      container.innerHTML = markup;
      const svg = container.querySelector('svg');
      if (!svg) throw new Error('SVG-Karte enthält kein SVG-Element');
      this.svg = svg;
      svg.classList.add('wine-atlas-fixed-map');
      svg.setAttribute('role', 'group');
      svg.setAttribute('aria-label', 'Japanische Weinregionen mit drei festen Kartenstufen');
      svg.querySelector('desc')?.replaceChildren(document.createTextNode('Alle 47 Präfekturen sind mit Maus, Touch, Enter oder Leertaste auswählbar.'));
      this.enhanceMap();
      container.setAttribute('aria-busy', 'false');
      this.update(this.matchingIds, state);
      this.setLevel(this.level, { animate: false });
    }

    getLayoutName() {
      const width = this.mapCanvas?.clientWidth || window.innerWidth;
      return width <= 600 ? 'mobile' : width <= 900 ? 'tablet' : 'desktop';
    }

    enhanceMap() {
      this.geometryById.clear();
      const viewBox = this.svg.viewBox?.baseVal;
      this.originalViewBox = viewBox?.width > 0
        ? { x: viewBox.x, y: viewBox.y, width: viewBox.width, height: viewBox.height }
        : { x: 0, y: 0, width: 1000, height: 1000 };
      this.svg.querySelectorAll('.prefecture[data-code]').forEach((element) => {
        const code = String(element.dataset.code || '').padStart(2, '0');
        const feature = this.features.find((item) => String(item.properties?.code || '').padStart(2, '0') === code);
        if (!feature) return;
        const item = this.regionForFeature(feature);
        element.classList.toggle('atlas-profiled', Boolean(item.region));
        this.bindInteractive(element, feature);
        const bounds = this.boundsForElement(element);
        if (bounds) this.geometryById.set(item.id, bounds);
      });
      this.addTouchTargets();
    }

    boundsForElement(element) {
      try {
        const box = element?.getBBox?.();
        if (!box || !Number.isFinite(box.width) || box.width <= 0 || box.height <= 0) return null;
        const matrix = element.getCTM?.();
        if (!matrix || !this.svg?.createSVGPoint) return { x: box.x, y: box.y, width: box.width, height: box.height };
        const corners = [
          [box.x, box.y], [box.x + box.width, box.y],
          [box.x + box.width, box.y + box.height], [box.x, box.y + box.height],
        ].map(([x, y]) => {
          const point = this.svg.createSVGPoint();
          point.x = x;
          point.y = y;
          return point.matrixTransform(matrix);
        });
        const xs = corners.map((point) => point.x);
        const ys = corners.map((point) => point.y);
        const x = Math.min(...xs);
        const y = Math.min(...ys);
        return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
      } catch (error) {
        return null;
      }
    }

    addTouchTargets() {
      const layer = this.createElement('g', { class: 'atlas-touch-layer', 'aria-hidden': 'true' });
      this.geometryById.forEach((bounds, id) => {
        const target = this.createElement('circle', {
          class: 'atlas-touch-target',
          cx: bounds.x + (bounds.width / 2),
          cy: bounds.y + (bounds.height / 2),
          r: this.getLayoutName() === 'mobile' ? 30 : 23,
          'data-touch-region': id,
        });
        const onClick = (event) => {
          event.stopPropagation();
          const element = this.svg.querySelector(`[data-atlas-region="${CSS.escape(id)}"]`);
          element?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        };
        target.addEventListener('click', onClick);
        this.listeners.push([target, 'click', onClick]);
        layer.appendChild(target);
      });
      this.svg.appendChild(layer);
      this.touchLayer = layer;
    }

    setLevel(level, options = {}) {
      this.level = ['japan', 'region', 'detail'].includes(level) ? level : 'japan';
      const selected = this.geometryById.get(this.selectedId);
      let target = this.originalViewBox;
      if (selected && this.level !== 'japan') {
        const factor = this.level === 'detail' ? 2.2 : 4.6;
        const width = Math.max(selected.width * factor, this.originalViewBox.width * (this.level === 'detail' ? 0.18 : 0.34));
        const height = Math.max(selected.height * factor, this.originalViewBox.height * (this.level === 'detail' ? 0.18 : 0.34));
        target = this.fitBounds({
          x: selected.x + (selected.width / 2) - (width / 2),
          y: selected.y + (selected.height / 2) - (height / 2),
          width,
          height,
        }, 0.08);
      }
      this.touchLayer?.toggleAttribute('hidden', this.level !== 'japan');
      this.animateViewBox(target, options.animate !== false);
    }

    zoomBy(delta) {
      this.scale = Math.min(2.6, Math.max(0.8, (this.scale || 1) + delta));
      if (this.mapStage) this.mapStage.style.transform = `scale(${this.scale})`;
      const reset = this.controller.querySelector('[data-atlas-zoom-reset]');
      if (reset) reset.textContent = `${Math.round(this.scale * 100)}%`;
    }

    resetView() {
      this.scale = 1;
      if (this.mapStage) this.mapStage.style.transform = 'none';
      const reset = this.controller.querySelector('[data-atlas-zoom-reset]');
      if (reset) reset.textContent = '100%';
      this.setLevel('japan');
    }

    resize() {
      this.controller.dataset.atlasLayout = this.getLayoutName();
      this.setLevel(this.level, { animate: false });
    }

    destroy() {
      if (this.mapStage) this.mapStage.style.transform = '';
      super.destroy();
    }
  }

  registry.FixedMapRenderer = FixedMapRenderer;
  registry['fixed-v3'] = FixedMapRenderer;
})();
